import { Form, FormikProvider, useFormik } from "formik";
import { z } from "zod";
import { OrganizationServiceCreateSchema } from "@common/contracts/organization-service.contract";
import { OrganizationService } from "@common/types/organizationService";
import {
    useCreateOrganizationServiceMutation,
    useUpdateOrganizationServiceMutation,
} from "@api/organizationService.api";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";

type ServiceFormValues = z.infer<typeof OrganizationServiceCreateSchema>;

const fields = createFormikFields<ServiceFormValues>();

interface Props {
    show: boolean;
    onHide: () => void;
    onCreated?: (service: OrganizationService) => void;
    service?: OrganizationService;
}

export const OrganizationServiceFormModal = ({
    show,
    onHide,
    onCreated,
    service,
}: Props) => {
    const { error: errorToast } = useToaster();
    const createService = useCreateOrganizationServiceMutation();
    const updateService = useUpdateOrganizationServiceMutation(service);

    const isEditMode = !!service;
    const isSubmitting = createService.isPending || updateService.isPending;

    const formik = useFormik<ServiceFormValues>({
        initialValues: {
            name: service?.name ?? "",
        },
        enableReinitialize: true,
        validate: validateFormikWithZodSchema(OrganizationServiceCreateSchema),
        validateOnMount: true,
        onSubmit: submitHelper({
            loading: isSubmitting,
            handler: async (values) => {
                const payload = { name: values.name.trim() };
                if (isEditMode) {
                    await updateService.mutateAsync(payload);
                    onHide();
                } else {
                    const created = await createService.mutateAsync(payload);
                    onHide();
                    onCreated?.(created);
                }
            },
            failureHandler: async () => {
                errorToast(
                    isEditMode
                        ? "Failed to update tier"
                        : "Failed to create tier",
                );
            },
        }),
    });

    return (
        <PlainModal show={show} onHide={onHide}>
            <StandardModalHeader>
                {isEditMode ? "Edit tier" : "Create tier"}
            </StandardModalHeader>
            <FormikProvider value={formik}>
                <Form onSubmit={formik.handleSubmit}>
                    <VStack gap={16}>
                        <fields.Text name="name" label="Name" size="xl" />
                        <HStack as="footer" justify="end" gap={12}>
                            <Button
                                variant="secondary"
                                size="xl"
                                type="button"
                                onClick={onHide}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                            <ButtonWithLoader
                                variant="primary"
                                size="xl"
                                type="submit"
                                loading={isSubmitting}
                                disabled={!formik.isValid}
                            >
                                {isEditMode ? "Save" : "Create"}
                            </ButtonWithLoader>
                        </HStack>
                    </VStack>
                </Form>
            </FormikProvider>
        </PlainModal>
    );
};
